import { useState } from 'react';
import { CheckCircle, Settings, FileText, BookOpen, BarChart2, Headphones, Tag, CreditCard, Download } from 'lucide-react';

export function SubscriptionManagement() {
  const plans = [
    { name: 'Free', active: 45200, revenue: 0, color: 'bg-[#E5E7EB]' },
    { name: 'Basic', active: 2100, revenue: 207900, color: 'bg-[#1E6DFF]/20' },
    { name: 'Pro', active: 1340, revenue: 333660, color: 'bg-[#1E6DFF]/60' },
    { name: 'Premium', active: 400, revenue: 199600, color: 'bg-[#1E6DFF]' },
  ];

  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl text-[#2D323A] mb-6">Subscription Management</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {plans.map(p => (
          <div key={p.name} className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <div className={`h-2 rounded-full mb-3 ${p.color}`} />
            <p className="text-2xl text-[#2D323A]">{p.active.toLocaleString()}</p>
            <p className="text-sm text-[#717182]">{p.name} Plan</p>
            {p.revenue > 0 && <p className="text-xs text-[#22C55E] mt-1">{p.revenue.toLocaleString()} MAD/mo</p>}
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <h2 className="text-[#2D323A] mb-4">Recent Subscription Changes</h2>
        <div className="space-y-3">
          {[
            { user: 'Karimi Motors', from: 'Business', to: 'Enterprise', date: 'Jun 5, 2024', amount: 1999 },
            { user: 'Fatima Z.', from: 'Free', to: 'Pro', date: 'Jun 3, 2024', amount: 249 },
            { user: 'Mohammed A.', from: 'Basic', to: 'Pro', date: 'Jun 1, 2024', amount: 249 },
          ].map((s, i) => (
            <div key={i} className="flex items-center justify-between py-3 border-b border-[#E5E7EB] last:border-0">
              <div>
                <p className="text-sm text-[#2D323A]">{s.user}</p>
                <p className="text-xs text-[#717182]">{s.from} → {s.to} · {s.date}</p>
              </div>
              <span className="text-sm text-[#22C55E]">+{s.amount} MAD</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PaymentManagement() {
  const payments = [
    { id: '#PAY-001', user: 'Karimi Motors', plan: 'Enterprise', amount: 1999, status: 'paid', date: 'Jun 5, 2024' },
    { id: '#PAY-002', user: 'Fatima Zahra', plan: 'Pro', amount: 249, status: 'paid', date: 'Jun 3, 2024' },
    { id: '#PAY-003', user: 'Atlas Auto', plan: 'Business', amount: 999, status: 'failed', date: 'Jun 2, 2024' },
    { id: '#PAY-004', user: 'Mohammed A.', plan: 'Pro', amount: 249, status: 'paid', date: 'Jun 1, 2024' },
  ];

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl text-[#2D323A]">Payment Management</h1>
        <button className="flex items-center gap-2 px-4 py-2 border border-[#E5E7EB] rounded-xl hover:bg-[#F5F7FA] text-sm text-[#2D323A]">
          <Download className="w-4 h-4" /> Export
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 px-5 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB] text-xs text-[#717182]">
          <span>ID</span><span>User</span><span>Plan</span><span>Amount</span><span>Date</span><span>Status</span>
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {payments.map((p, i) => (
            <div key={i} className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 items-center px-5 py-3">
              <span className="text-xs text-[#717182]">{p.id}</span>
              <span className="text-sm text-[#2D323A]">{p.user}</span>
              <span className="text-xs px-2 py-0.5 bg-[#F5F7FA] text-[#717182] rounded-lg">{p.plan}</span>
              <span className="text-sm text-[#2D323A]">{p.amount} MAD</span>
              <span className="text-xs text-[#717182]">{p.date}</span>
              <span className={`text-xs px-2 py-0.5 rounded-lg ${p.status === 'paid' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`}>{p.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CMSManagement() {
  const pages = ['Homepage Hero', 'About Us', 'Contact Page', 'FAQ', 'Privacy Policy', 'Terms & Conditions', 'Pricing Page'];
  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl text-[#2D323A] mb-6">CMS Management</h1>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#E5E7EB]">
        {pages.map(page => (
          <div key={page} className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-[#1E6DFF]" />
              <span className="text-sm text-[#2D323A]">{page}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-xs px-2 py-0.5 bg-[#22C55E]/10 text-[#22C55E] rounded-lg">Published</span>
              <button className="text-sm text-[#1E6DFF] hover:underline">Edit</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BlogManagement() {
  const posts = [
    { title: 'Best Cars in Morocco 2024', author: 'Karim A.', status: 'published', views: 1234, date: 'Jun 1, 2024' },
    { title: 'How to Sell Your Car Faster', author: 'Sara B.', status: 'published', views: 892, date: 'May 25, 2024' },
    { title: 'Electric Vehicles in Morocco', author: 'Amine T.', status: 'draft', views: 0, date: 'Jun 6, 2024' },
  ];
  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl text-[#2D323A]">Blog Management</h1>
        <button className="px-4 py-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] text-sm">New Post</button>
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#E5E7EB]">
        {posts.map((p, i) => (
          <div key={i} className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="text-sm text-[#2D323A]">{p.title}</p>
              <p className="text-xs text-[#717182]">{p.author} · {p.date}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#717182]">{p.views.toLocaleString()} views</span>
              <span className={`text-xs px-2 py-0.5 rounded-lg ${p.status === 'published' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#F59E0B]/10 text-[#F59E0B]'}`}>{p.status}</span>
              <button className="text-sm text-[#1E6DFF] hover:underline">Edit</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Reports() {
  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl text-[#2D323A] mb-6">Reports</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { title: 'User Growth Report', desc: 'Monthly new registrations and retention', icon: '👥' },
          { title: 'Listings Report', desc: 'Active, expired, and sold listings', icon: '🚗' },
          { title: 'Revenue Report', desc: 'Subscription and transaction revenue', icon: '💰' },
          { title: 'Lead Reports', desc: 'Contact rates and conversion data', icon: '📊' },
          { title: 'Fraud Report', desc: 'Flagged accounts and listings', icon: '🔒' },
          { title: 'Support Report', desc: 'Ticket volume and resolution time', icon: '🎧' },
        ].map(r => (
          <div key={r.title} className="bg-white rounded-2xl border border-[#E5E7EB] p-5 hover:shadow-md transition-shadow cursor-pointer">
            <div className="text-3xl mb-3">{r.icon}</div>
            <h3 className="text-[#2D323A] mb-1">{r.title}</h3>
            <p className="text-sm text-[#717182] mb-4">{r.desc}</p>
            <button className="flex items-center gap-1 text-sm text-[#1E6DFF] hover:underline">
              <Download className="w-4 h-4" /> Download CSV
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SupportTickets() {
  const tickets = [
    { id: '#TKT-001', user: 'Mohammed A.', subject: 'Payment not processed', priority: 'high', status: 'open', date: 'Jun 5, 2024' },
    { id: '#TKT-002', user: 'Fatima Z.', subject: 'Cannot upload photos', priority: 'medium', status: 'in-progress', date: 'Jun 4, 2024' },
    { id: '#TKT-003', user: 'Karimi Motors', subject: 'Subscription upgrade issue', priority: 'low', status: 'resolved', date: 'Jun 3, 2024' },
  ];
  const priorityColors: Record<string, string> = { high: 'text-[#EF4444] bg-[#EF4444]/10', medium: 'text-[#F59E0B] bg-[#F59E0B]/10', low: 'text-[#22C55E] bg-[#22C55E]/10' };
  const statusColors: Record<string, string> = { open: 'bg-[#EF4444]/10 text-[#EF4444]', 'in-progress': 'bg-[#F59E0B]/10 text-[#F59E0B]', resolved: 'bg-[#22C55E]/10 text-[#22C55E]' };
  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl text-[#2D323A] mb-6">Support Tickets</h1>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#E5E7EB]">
        {tickets.map((t, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4">
            <span className="text-xs text-[#717182] w-20">{t.id}</span>
            <div className="flex-1">
              <p className="text-sm text-[#2D323A]">{t.subject}</p>
              <p className="text-xs text-[#717182]">{t.user} · {t.date}</p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-lg ${priorityColors[t.priority]}`}>{t.priority}</span>
            <span className={`text-xs px-2 py-0.5 rounded-lg ${statusColors[t.status]}`}>{t.status}</span>
            <button className="text-sm text-[#1E6DFF] hover:underline">View</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SystemSettings() {
  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl text-[#2D323A] mb-6">System Settings</h1>
      <div className="space-y-5">
        {[
          { title: 'General Settings', items: ['Platform Name', 'Default Language', 'Currency', 'Time Zone'] },
          { title: 'Email Settings', items: ['SMTP Configuration', 'Email Templates', 'Notification Rules'] },
          { title: 'Payment Settings', items: ['Payment Gateways', 'Currency Settings', 'Tax Configuration'] },
          { title: 'Security Settings', items: ['Two-Factor Auth Policy', 'Session Timeout', 'IP Whitelist', 'Rate Limiting'] },
        ].map(section => (
          <div key={section.title} className="bg-white rounded-2xl border border-[#E5E7EB]">
            <div className="px-5 py-3 border-b border-[#E5E7EB]">
              <h3 className="text-[#2D323A]">{section.title}</h3>
            </div>
            <div className="divide-y divide-[#E5E7EB]">
              {section.items.map(item => (
                <div key={item} className="flex items-center justify-between px-5 py-3">
                  <span className="text-sm text-[#2D323A]">{item}</span>
                  <button className="text-sm text-[#1E6DFF] hover:underline">Configure</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
